import {
  AppBridge,
  PostMessageTransport,
  type McpUiDisplayMode,
  type McpUiSandboxProxyReadyNotification,
} from '@modelcontextprotocol/ext-apps/app-bridge';
import { useEffect, useRef, useState } from 'react';

import {
  reportMcpChartAppLog,
  reportMcpChartFailure,
} from '@/hooks/useAuthWebSocket/mcpChartObservability';
import {
  getCachedAppBundleHtml,
  getMcpServerHttpOriginFromWsUrl,
} from '@/hooks/useAuthWebSocket/message/appBundle';
import { cn } from '@/lib/utils';
import { useTheme } from '@/providers/Theme';

import {
  applyAppSizeToHostIframe,
  createMcpAppHostContext,
  createToolResultFromAppPayload,
  downloadMcpAppFileContents,
  getAppResourceSecurity,
  getMcpChartRenderState,
  getMcpHostCapabilities,
  injectCspMetaTag,
  MCP_SANDBOX_PROXY_SANDBOX,
  MCP_SUPPORTED_DISPLAY_MODES,
  resolveMcpDisplayModeRequest,
} from './mcpChartHostUtils';

const HOST_INFO = { name: 'Channel99', version: '1.0.0' }; // eslint-disable-line lingui/no-unlocalized-strings
const SANDBOX_PROXY_READY: McpUiSandboxProxyReadyNotification['method'] =
  'ui/notifications/sandbox-proxy-ready';
const SANDBOX_PROXY_TIMEOUT_MS = 10_000;
export const SANDBOX_PROXY_PING = 'channel99/mcp-sandbox-proxy-ping';

export const getMcpSandboxProxyUrl = () => {
  const sandboxOrigin =
    getMcpServerHttpOriginFromWsUrl(import.meta.env.VITE_MCP_SERVER_WS_URL) ??
    window.location.origin;
  const url = new URL('/mcp-sandbox.html', sandboxOrigin);
  url.searchParams.set('hostOrigin', window.location.origin);
  return url.href;
};

export const loadMcpSandboxProxy = (iframe: HTMLIFrameElement): Promise<void> => {
  iframe.setAttribute('sandbox', MCP_SANDBOX_PROXY_SANDBOX);

  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      window.removeEventListener('message', listener);
      reject(new Error('MCP sandbox proxy failed to initialize'));
    }, SANDBOX_PROXY_TIMEOUT_MS);

    const listener = ({ source, data }: MessageEvent) => {
      if (source === iframe.contentWindow && data?.method === SANDBOX_PROXY_READY) {
        window.clearTimeout(timeoutId);
        window.removeEventListener('message', listener);
        resolve();
      }
    };

    window.addEventListener('message', listener);

    const sandboxUrl = getMcpSandboxProxyUrl();
    if (iframe.src === sandboxUrl) {
      iframe.contentWindow?.postMessage(SANDBOX_PROXY_PING, '*');
      return;
    }

    iframe.src = sandboxUrl;
  });
};

type Props = {
  resourceUri: string;
  toolName: string;
  structuredContent: unknown;
};

const McpChartHost = ({ resourceUri, toolName, structuredContent }: Props) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const bridgeRef = useRef<AppBridge | undefined>(undefined);
  const teardownAbortRef = useRef<AbortController | undefined>(undefined);
  const displayModeRef = useRef<McpUiDisplayMode>('inline');
  const { theme } = useTheme();
  const renderState = getMcpChartRenderState({ resourceUri, structuredContent });
  const [isVisible, setIsVisible] = useState(renderState.renderable);
  const [prevRenderable, setPrevRenderable] = useState(renderState.renderable);
  const [displayMode, setDisplayMode] = useState<McpUiDisplayMode>('inline');
  const bundleHtml = getCachedAppBundleHtml(resourceUri);

  if (renderState.renderable !== prevRenderable) {
    setPrevRenderable(renderState.renderable);
    setIsVisible(renderState.renderable);
  }

  useEffect(() => {
    const iframe = iframeRef.current;
    const resourceSecurity = getAppResourceSecurity(resourceUri);

    if (
      !iframe ||
      !isVisible ||
      bundleHtml === undefined ||
      bundleHtml.trim().length === 0 ||
      !resourceSecurity
    ) {
      return;
    }

    let cancelled = false;
    let guestInitialized = false;

    const mountChart = async () => {
      await loadMcpSandboxProxy(iframe);
      if (cancelled) {
        return;
      }

      const bridge = new AppBridge(null, HOST_INFO, getMcpHostCapabilities(), {
        hostContext: createMcpAppHostContext(theme),
      });
      bridgeRef.current = bridge;

      bridge.onsizechange = ({ width, height }) => {
        applyAppSizeToHostIframe(iframe, { width, height }, displayModeRef.current);
      };

      bridge.onloggingmessage = ({ level, logger, data }) => {
        reportMcpChartAppLog({
          level,
          logger,
          data,
          resourceUri,
          toolName,
        });
      };

      bridge.ondownloadfile = async ({ contents }) => {
        if (cancelled) {
          return { isError: true };
        }

        return downloadMcpAppFileContents(contents);
      };

      bridge.onrequestdisplaymode = async ({ mode }) => {
        if (cancelled) {
          return { mode: displayModeRef.current };
        }

        const resolvedMode = resolveMcpDisplayModeRequest(
          mode,
          MCP_SUPPORTED_DISPLAY_MODES,
          displayModeRef.current,
        );

        displayModeRef.current = resolvedMode;
        setDisplayMode(resolvedMode);
        applyAppSizeToHostIframe(iframe, {}, resolvedMode);
        bridge.setHostContext(createMcpAppHostContext(theme, resolvedMode));

        return { mode: resolvedMode };
      };

      const initialized = new Promise<void>((resolve) => {
        bridge.oninitialized = () => {
          guestInitialized = true;
          resolve();
        };
      });

      await bridge.connect(new PostMessageTransport(iframe.contentWindow!, iframe.contentWindow!));

      await bridge.sendSandboxResourceReady({
        html: injectCspMetaTag(bundleHtml, resourceSecurity.csp),
        sandbox: resourceSecurity.sandbox,
        csp: resourceSecurity.csp,
        permissions: resourceSecurity.permissions,
      });

      await initialized;

      if (cancelled) {
        return;
      }

      bridge.sendToolInput({ arguments: {} });
      bridge.sendToolResult(createToolResultFromAppPayload({ toolName, structuredContent }));
    };

    const runMount = async () => {
      try {
        await mountChart();
      } catch (error) {
        setIsVisible(false);
        reportMcpChartFailure({
          stage: 'mount_failed',
          resourceUri,
          toolName,
          error,
        });
      }
    };

    runMount();

    return () => {
      cancelled = true;
      displayModeRef.current = 'inline';
      setDisplayMode('inline');
      iframe.style.height = '';
      iframe.style.minWidth = '';

      teardownAbortRef.current?.abort();
      teardownAbortRef.current = undefined;

      const bridge = bridgeRef.current;
      bridgeRef.current = undefined;
      const guestCanRespond =
        guestInitialized && iframe.isConnected && iframe.contentWindow != null;

      const runTeardown = async () => {
        if (bridge && guestCanRespond) {
          const teardownAbort = new AbortController();
          teardownAbortRef.current = teardownAbort;

          try {
            await bridge.teardownResource({}, { signal: teardownAbort.signal });
          } catch (error) {
            if (!teardownAbort.signal.aborted && iframe.isConnected) {
              reportMcpChartFailure({
                stage: 'teardown_failed',
                resourceUri,
                toolName,
                error,
              });
            }
          }
        }

        try {
          await bridge?.close();
        } catch {
          // Bridge may not have connected if mount was cancelled early.
        }
      };

      runTeardown();
    };
  }, [bundleHtml, isVisible, resourceUri, structuredContent, theme, toolName]);

  useEffect(() => {
    if (displayMode !== 'fullscreen') {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [displayMode]);

  if (
    !isVisible ||
    bundleHtml === undefined ||
    bundleHtml.trim().length === 0 ||
    !getAppResourceSecurity(resourceUri)
  ) {
    return null;
  }

  return (
    <div
      data-testid="mcp-chart-host"
      data-display-mode={displayMode}
      className={cn(
        'w-full',
        displayMode === 'fullscreen' &&
          'fixed inset-0 z-1200 flex flex-col bg-white dark:bg-gray-900',
      )}
    >
      <iframe
        ref={iframeRef}
        title="SQL results chart" // eslint-disable-line lingui/no-unlocalized-strings
        className={cn('min-h-48 w-full border-0', displayMode === 'fullscreen' && 'min-h-0 flex-1')}
      />
    </div>
  );
};

export default McpChartHost;
